import { useEffect, useState } from 'react';
import { Layout } from '@/components/layout';
import { Card, Button, EmptyState, ErrorState, Spinner } from '@/components/atoms';
import { useBackButton, useHapticFeedback } from '@/hooks';
import { savedSearches } from '@/api';
import type { SavedSearch } from '@/types/domain';
import { logger } from '@/utils/logger';
import { toast } from '@/components/atoms/Toast';
import styles from './pages.module.css';

export function SavedSearchesPage(): JSX.Element {
  useBackButton();
  const haptic = useHapticFeedback();
  const [items, setItems] = useState<SavedSearch[] | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const load = async (): Promise<void> => {
    setError(null);
    try {
      const data = await savedSearches.fetchSavedSearches();
      setItems(data.results);
    } catch (err) {
      logger.warn('Saved searches fetch failed', err);
      setError(err instanceof Error ? err : new Error(String(err)));
      setItems(null);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const remove = async (id: number): Promise<void> => {
    try {
      await savedSearches.deleteSavedSearch(id);
      // F-180 C8: haptic confirmation on delete success.
      haptic.notification('success');
      toast.info('Видалено');
      void load();
    } catch (err) {
      logger.warn('Saved search delete failed', err);
      toast.error('Не вдалось видалити');
    }
  };

  if (error)
    return (
      <Layout title="Збережені пошуки">
        <ErrorState onRetry={() => void load()} />
      </Layout>
    );
  if (items === null) return <Layout title="Збережені пошуки"><Spinner center /></Layout>;
  if (items.length === 0)
    return (
      <Layout title="Збережені пошуки">
        <EmptyState
          icon="saved"
          title="Жодного збереженого пошуку"
          message="Знайди когось, тапни «Зберегти пошук» — і алерти приходитимуть."
        />
      </Layout>
    );

  return (
    <Layout title="Збережені пошуки">
      <div className={styles.list}>
        {items.map((s) => (
          <Card key={s.id}>
            <strong>{s.name}</strong>
            <p className={styles.note}>
              {s.delivery_mode === 'instant' ? 'Миттєві алерти' : 'Щоденний дайджест'}
            </p>
            <div className={styles.actions}>
              <Button variant="destructive" onClick={() => remove(s.id)}>
                Видалити
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </Layout>
  );
}
